/**
 * The i18n shim. Same signature as the Portal's hook, one language.
 *
 * Fourteen vendored components call `useLocale()`, so the shape has to match
 * or the toolkit does not compile. What is deliberately absent is the
 * machinery: no bundles to keep in parity, no locale switch, no second
 * language. The consoles are English only by programme decision.
 *
 * DIVERGENCE from the console design package, reported rather than resolved
 * silently: the package says `t(key)` should interpolate over the key itself.
 * Taken literally that puts "common.close" on a button and "common.loading" on
 * a spinner, because the toolkit uses these keys for visible text and not only
 * for aria labels. So every key the vendored subset uses carries its upstream
 * English string, copied verbatim from the Portal's en.json at the vendored
 * commit. Anything else falls back to the key, which is the package's
 * behaviour and is now a visible signal that a new key arrived with a toolkit
 * update.
 *
 * Not every key is passed to `t` as a literal: FreshnessChip and Severity look
 * theirs up through a table, so a grep for `t('...')` misses them and the
 * fallback showed a key on screen. The conformance script now harvests every
 * key-shaped literal in `platform/` and fails on one this file does not carry.
 */

import { useCallback } from 'react'

type Params = Record<string, string | number>

/** Copied from Wall-E Service-Platform en.json at ed1d30a9. */
const STRINGS: Record<string, string> = {
  'billing.history.title': 'Submission history',
  'billing.preview.column.description': 'Description',
  'common.aria.moreActions': '{{count}} more actions',
  'common.aria.removeItem': 'Remove {{item}}',
  'common.close': 'Close',
  'common.image': 'Image',
  'common.item': 'item',
  'common.loading': 'Loading',
  'console.nav.close': 'Close navigation',
  'console.nav.collapse': 'Collapse the navigation rail',
  'console.nav.collapseShort': 'Collapse',
  'console.nav.expand': 'Keep the navigation rail open',
  'console.nav.label': 'Console sections',
  'console.nav.menu': 'Open navigation',
  'console.nav.signOut': 'Sign out',
  'common.time.daysAgo': '{{n}}d ago',
  'common.time.hoursAgo': '{{n}}h ago',
  'common.time.minutesAgo': '{{n}}m ago',
  'common.time.secondsAgo': '{{n}}s ago',
  'shell.banner.dismiss': 'Dismiss banner',
  'shell.error.generic': 'Something went wrong',
  'shell.error.tryAgain': 'Try again',
  'shell.rail.collapse': 'Collapse side panel',
  'shell.rail.expand': 'Expand side panel',
  'shell.rail.label': 'App side rail',
  'split.resize': 'Resize the left pane',
  'widget.freshness.error.label': 'Error',
  'widget.freshness.error.tooltip': 'Last fetch failed',
  'widget.freshness.live.label': 'Live',
  'widget.freshness.live.tooltip': 'Data is fresh and updating',
  'widget.freshness.paused.label': 'Paused',
  // The one edit made to any copied string. Upstream separates the two
  // clauses with a dash this programme does not use in text that reaches a
  // reader (rule 11 of the console design package), so it becomes a comma.
  // Reported to the orchestrator, not resolved silently.
  'widget.freshness.paused.tooltip': 'Polling is paused, refresh to continue',
  'widget.freshness.stale.label': 'Stale',
  'widget.freshness.stale.tooltip': 'Data is older than the freshness window',
  'widget.freshness.tooltipUpdated': '{{tooltip}}, updated {{updated}}',
  'widget.severity.critical': 'P1 Critical',
  'widget.severity.high': 'P2 High',
  'widget.severity.info': 'Info',
  'widget.severity.low': 'P4 Low',
  'widget.severity.medium': 'P3 Medium',
}

export function translate(key: string, params?: Params): string {
  let text = STRINGS[key] ?? key
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      text = text.replace(new RegExp(`\\{\\{${name}\\}\\}`, 'g'), String(value))
    }
  }
  return text
}

export function useLocale() {
  // Stable across renders on purpose: the vendored components put `t` in
  // dependency arrays, and a new function each render would re-run their
  // effects on every tick.
  const t = useCallback(translate, [])
  return { t, locale: 'en' as const, setLocale: () => undefined }
}
